/**
 * ATTEST-UX-2 D1 — the "(i)" copy deck for Steps 1–3.
 *
 * Step 4 already had one (`independenceHelp.ts`). The other three steps ask the
 * CPA for things that decide what the letter may say — the basis, the requested
 * items, the assertion behind each statement — and answered nothing about why.
 * A form that gates on an answer it never explained teaches the CPA to guess
 * until the gate opens, which is the opposite of what a compliance screen is
 * for.
 *
 * Same shape and same reason as `independenceHelp.ts`: the strings live in one
 * module rather than inline in the JSX so the sentence on the field and the
 * sentence in the refusal can be kept in step. Help text that drifts from the
 * gate is worse than no help text.
 *
 * Only fields that genuinely need explaining get an entry. Subject entity and
 * addressee do not — an (i) on an obvious field is noise, and noise is what
 * makes the CPA stop clicking the ones that matter.
 */
import type { FieldHelp } from '@/components/attest/independenceHelp'

export const WIZARD_FIELD_HELP = {
  // --- Step 1: classify the request -------------------------------------
  letterType: {
    label: 'Letter type',
    paragraphs: [
      'What kind of verification letter this is. It fixes what the letter may say, which disclaimers it carries, and the standard it’s issued under. Everything downstream depends on it.',
    ],
  },
  requestedItems: {
    label: 'What is the requester asking you to confirm?',
    paragraphs: [
      'Paste the requester’s exact wording, one item per line — don’t paraphrase. The system scans what was actually asked, so a prohibited request gets caught here with a safe alternative. An empty box means there’s nothing to confirm.',
    ],
  },
  engagementBasis: {
    label: 'Engagement basis',
    paragraphs: [
      'On what basis can this firm speak to these tax years — you prepared them, they were provided to you (not prepared/audited), or you performed procedures. You’ll attach the supporting documents in Step 3; this just states your relationship to them, and the letter is held to it.',
    ],
  },
  assuranceLevel: {
    label: 'Assurance the requester asked for',
    paragraphs: [
      'Optional. If the requester asked for assurance beyond plain facts, note it — the letter cannot exceed what the basis supports, and this helps the classifier catch an over-ask.',
    ],
  },

  // --- Step 2: what may be said -----------------------------------------
  allowedStatements: {
    label: 'What this letter may state',
    paragraphs: [
      'The only statements this letter type can make. The greyed items are hard-blocked — a CPA can’t support them — so you build the letter from the allowed list.',
    ],
  },

  // --- Step 3: letter and statements ------------------------------------
  intendedUsers: {
    label: 'Intended users',
    paragraphs: [
      'Who may rely on the letter. It sets the restricted-use paragraph — anyone not listed has no right to rely on it.',
    ],
  },
  taxYears: {
    label: 'Tax years in scope for this engagement',
    paragraphs: [
      'e.g. “2022, 2023 and 2024”. Bounds every statement — a statement about a year outside this list is blocked.',
    ],
  },
  statementAssertion: {
    label: 'Statement → Assertion',
    paragraphs: [
      'Pick from the allowed list; each is a fact the CPA can support. The sentence is what actually prints.',
    ],
  },
  evidence: {
    label: 'Evidence',
    paragraphs: [
      'The source that backs the statement. The document itself has to say it — a citation isn’t verification. Attach a text-based file the system can read.',
    ],
  },
  clientConsent: {
    label: 'Client consent on file',
    paragraphs: [
      'IRC §7216 / ET 1.700: releasing tax-return information to a third party needs the client’s written consent. The letter won’t finalize without it.',
    ],
  },
} satisfies Record<string, FieldHelp>

export type WizardHelpField = keyof typeof WIZARD_FIELD_HELP

/**
 * Required-input key → help entry.
 *
 * Step 3 renders `required_inputs` from the letter-type YAML in a generic loop,
 * so the tax-years help has to be keyed off the input's key rather than off a
 * label the registry owns. A key with no entry simply gets no (i) — which is
 * the right default for an input a future letter type adds.
 */
export const INPUT_FIELD_HELP: Partial<Record<string, WizardHelpField>> = {
  tax_years_prepared: 'taxYears',
}
